/**
 * The latest Bar Intelligence numbers per chart group, for the data panel.
 *
 * Module scope with subscribers — `symbol-index.ts`' shape. The chart computes
 * the numbers (it holds the bars); the panel is mounted elsewhere and only
 * knows which chart group it describes, so the chart publishes here and the
 * panel subscribes by id. Nothing is persisted: it is a view of the chart.
 */

import { useSyncExternalStore } from "react";
import type { BarIntelSnapshot } from "./bar-intelligence-view";

const snapshots = new Map<string, BarIntelSnapshot>();
const listeners = new Set<() => void>();

function emit(): void {
  for (const fn of listeners) fn();
}

/** Publish (or, with `undefined`, withdraw) one chart group's numbers. */
export function publishBarIntel(chartGroupId: string, snapshot: BarIntelSnapshot | undefined): void {
  if (snapshot === undefined) {
    if (!snapshots.has(chartGroupId)) return;
    snapshots.delete(chartGroupId);
  } else {
    snapshots.set(chartGroupId, snapshot);
  }
  emit();
}

export function barIntelSnapshot(chartGroupId: string | undefined): BarIntelSnapshot | undefined {
  return chartGroupId === undefined ? undefined : snapshots.get(chartGroupId);
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** The snapshot for one chart group, re-rendering when the chart publishes. */
export function useBarIntel(chartGroupId: string | undefined): BarIntelSnapshot | undefined {
  return useSyncExternalStore(
    subscribe,
    () => barIntelSnapshot(chartGroupId),
    () => undefined,
  );
}
